import React from 'react';
import { Check, Eye, Pencil, ThumbsDown, Trash2, Loader2, Copy, GitCompare } from 'lucide-react';
import TemplateThumbnail from '../../../components/shared/TemplateThumbnail';
import {
  STATUS_BADGE, STATUS_LABEL, canApprove, canReject, canPublish,
  pagesOf, elementsOf, canvasOf, formatDate,
} from './reviewConstants';

/**
 * One template in the review grid.
 *
 * The thumbnail is the app's own live render of the stored document, so what a
 * reviewer judges here is what the editor and the export will produce. The
 * card stays a fixed set of facts and five actions at every density; only its
 * width changes.
 */
const IconButton = ({ label, onClick, disabled, tone = 'gray', children }) => {
  const tones = {
    gray: 'text-gray-600 hover:bg-gray-100',
    green: 'text-emerald-700 hover:bg-emerald-50',
    red: 'text-red-600 hover:bg-red-50',
    violet: 'text-violet-700 hover:bg-violet-50',
  };
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`flex-1 h-8 inline-flex items-center justify-center rounded-md transition-colors disabled:opacity-40 ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

const ReviewCard = ({
  template, selected, busy, compact,
  onToggle, onPreview, onEdit, onApprove, onPublish, onReject, onDelete, onDuplicate,
}) => {
  const status = template.status || 'draft';
  const canvas = canvasOf(template);
  const elements = elementsOf(template).length;
  const pages = pagesOf(template).length;
  const similar = template.similar_templates?.length || 0;

  return (
    <article
      data-testid="review-card"
      data-template-id={template.id}
      className={`group bg-white rounded-xl border overflow-hidden flex flex-col transition-shadow hover:shadow-md ${
        selected ? 'border-violet-500 ring-2 ring-violet-200' : 'border-gray-200'
      }`}
    >
      <div className="relative bg-gray-100">
        <button
          type="button"
          onClick={() => onPreview(template)}
          className="block w-full cursor-zoom-in"
          aria-label={`Preview ${template.name}`}
        >
          <TemplateThumbnail template={template} />
        </button>

        <label className="absolute top-2 left-2 flex items-center justify-center w-6 h-6 rounded-md bg-white/95 shadow-sm cursor-pointer">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(template.id)}
            aria-label={`Select ${template.name}`}
            className="w-4 h-4 accent-violet-600 cursor-pointer"
          />
        </label>

        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[status] || STATUS_BADGE.draft}`}>
          {STATUS_LABEL[status] || status}
        </span>

        {similar > 0 && (
          <span
            title={`${similar} structurally similar template${similar === 1 ? '' : 's'} — open the preview to compare`}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800"
          >
            <GitCompare className="w-3 h-3" /> {similar}
          </span>
        )}
      </div>

      <div className="p-3 flex-1 flex flex-col gap-1.5 min-w-0">
        <h3 className="text-sm font-medium text-gray-900 leading-snug line-clamp-2" title={template.name}>
          {template.name}
        </h3>

        {(template.category || template.subcategory) && (
          <p className="text-[11px] text-gray-500 truncate" title={`${template.category || ''} › ${template.subcategory || ''}`}>
            {template.category}{template.category && template.subcategory ? ' › ' : ''}
            <span className="text-gray-700">{template.subcategory}</span>
          </p>
        )}

        {!compact && (
          <div className="flex flex-wrap gap-1">
            {template.style && (
              <span className="text-[10px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">{template.style}</span>
            )}
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded tabular-nums">{canvas.width}×{canvas.height}</span>
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded tabular-nums">{elements} elements</span>
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded tabular-nums">{pages} {pages === 1 ? 'page' : 'pages'}</span>
          </div>
        )}

        <p className="text-[10px] text-gray-400 mt-auto pt-1">{formatDate(template.created_at)}</p>
      </div>

      <div className="px-2 pb-2 flex items-center gap-0.5 border-t border-gray-100 pt-2">
        {busy ? (
          <div className="flex-1 h-8 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-violet-500" /></div>
        ) : (
          <>
            <IconButton label="Preview" onClick={() => onPreview(template)}><Eye className="w-4 h-4" /></IconButton>
            <IconButton label="Edit in canvas editor" onClick={() => onEdit(template)}><Pencil className="w-4 h-4" /></IconButton>
            {canPublish(status)
              ? <IconButton label="Publish (make public)" tone="green" onClick={() => onPublish(template)}><Check className="w-4 h-4" /><Check className="w-4 h-4 -ml-2.5" /></IconButton>
              : <IconButton label="Approve" tone="green" disabled={!canApprove(status)} onClick={() => onApprove(template)}><Check className="w-4 h-4" /></IconButton>}
            <IconButton label="Reject" tone="red" disabled={!canReject(status)} onClick={() => onReject(template)}><ThumbsDown className="w-4 h-4" /></IconButton>
            {!compact && <IconButton label="Duplicate" onClick={() => onDuplicate(template)}><Copy className="w-4 h-4" /></IconButton>}
            <IconButton label="Delete" tone="red" onClick={() => onDelete(template)}><Trash2 className="w-4 h-4" /></IconButton>
          </>
        )}
      </div>
    </article>
  );
};

export default React.memo(ReviewCard);
